/**
 * Analytics (§40).
 *
 * The constraints are the interesting part:
 *
 *   §40 "Do not send auth tokens, raw images/audio, or unnecessary sensitive
 *   child content to analytics."
 *   §40 "Prevent duplicate event firing from rerenders."
 *   §52 "Do not block Home on non-critical analytics."
 *
 * So: a scrubber that drops anything token-shaped or long enough to be
 * content, a dedupe key for once-per-view events, and a queue that batches to
 * the backend's own `/v1/events` rather than a third party (§42: "Minimize
 * third-party tracking" — a child's learning data should not leave our
 * infrastructure to be someone's ad signal).
 */

import { eventsApi } from '@/api/endpoints';

/** The taxonomy from §40, kept in step with the backend's event_definitions. */
export type EventName =
  | 'signup_completed'
  | 'home_viewed'
  | 'homework_started'
  | 'homework_image_selected'
  // What the downscale saves. The pair is what justifies it on an Indian
  // mobile connection: a 10MB page went up untouched before this.
  | 'homework_image_shrunk'
  | 'homework_voice_started'
  | 'homework_submitted'
  | 'homework_classified'
  // §13.5b. The pair is the funnel that matters: a picker shown and never
  // answered is a child who could not find their question in the list.
  | 'homework_question_picker_shown'
  | 'homework_question_selected'
  // The pair the mastery rule reads: asking is free the first couple of
  // times on a skill, and the count is what makes "stuck again and again"
  // visible rather than inferred.
  | 'practice_help_asked'
  // The money funnel (§40). Shown -> started -> activated, where the last of
  // the three is emitted server-side by the webhook, because it is the only
  // one of them that means anybody paid.
  | 'subscribe_banner_shown'
  | 'checkout_started'
  | 'homework_answer_viewed'
  | 'homework_answer_attempted'
  | 'homework_answer_revealed'
  | 'homework_doubt_asked'
  | 'skill_chat_asked'
  | 'voice_played'
  | 'practice_started'
  | 'practice_attempted'
  | 'practice_completed'
  | 'recommendation_shown'
  | 'recommendation_clicked'
  | 'memory_recall_triggered'
  | 'memory_recall_success'
  | 'mastery_updated'
  | 'parent_progress_viewed'
  // The pair that says whether the standing link is worth the credential it
  // puts in a chat thread: how many parents send one, and how many think
  // better of it afterwards.
  | 'child_sign_in_link_sent'
  | 'child_sign_in_link_revoked'
  | 'paywall_viewed'
  | 'subscription_started'
  | 'refund_requested'
  | 'language_selected'
  | 'language_switched'
  | 'session_started'
  | 'session_ended';

type Props = Record<string, string | number | boolean | null | undefined>;

/**
 * Anything longer than this is content, not a property. A homework question,
 * a transcript or a tutor explanation would all exceed it — and none of them
 * belong in analytics (§23, §40).
 */
const MAX_VALUE_LENGTH = 120;

const TOKEN_LIKE = /(token|secret|password|authorization|bearer|refresh|jwt|key)/i;

/**
 * Drop what must never be sent, and truncate what is merely long.
 *
 * Belt and braces: the call sites already pass ids rather than content, but a
 * future caller will eventually pass the wrong thing, and this is where that
 * mistake stops.
 */
export function scrub(props: Props): Props {
  const safe: Props = {};
  for (const [key, value] of Object.entries(props)) {
    if (value === undefined || value === null) continue;
    if (TOKEN_LIKE.test(key)) continue;
    if (typeof value === 'string') {
      if (value.length > MAX_VALUE_LENGTH) continue; // content, not a property
      safe[key] = value;
      continue;
    }
    safe[key] = value;
  }
  return safe;
}

interface QueuedEvent extends Record<string, unknown> {
  event_name: EventName;
  occurred_at: string;
  properties: Props;
  client_event_id: string;
}

const queue: QueuedEvent[] = [];
/** §40: "Prevent duplicate event firing from rerenders." */
const firedOnce = new Set<string>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleFlush(): void {
  if (flushTimer) return;
  // Batched and deferred, so a Home render never waits on analytics (§52).
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flush();
  }, 2000);
}

export async function flush(): Promise<void> {
  if (queue.length === 0) return;
  const batch = queue.splice(0, queue.length);
  try {
    await eventsApi.ingest(batch);
  } catch {
    // Analytics must never surface an error to a child, and a dropped batch
    // is not worth a retry storm on a poor connection.
  }
}

export function track(name: EventName, props: Props = {}): void {
  queue.push({
    event_name: name,
    occurred_at: new Date().toISOString(),
    properties: scrub(props),
    // The backend dedupes on this, so a retried batch cannot double-count.
    client_event_id: crypto.randomUUID(),
  });
  scheduleFlush();
}

/**
 * Fire at most once per `key` for the lifetime of the page.
 *
 * `recommendation_shown` is the motivating case: it renders on every Home
 * paint, and counting it each time would make the §41 funnel meaningless.
 */
export function trackOnce(key: string, name: EventName, props: Props = {}): void {
  if (firedOnce.has(key)) return;
  firedOnce.add(key);
  track(name, props);
}

/**
 * Called on sign-out: the next child must not inherit the last one's dedupe
 * keys — or their queued events. Cancelling the pending flush is the part
 * that matters: a batch scheduled before sign-out would otherwise post two
 * seconds later, carrying one child's activity under the next child's token.
 */
export function resetAnalytics(): void {
  firedOnce.clear();
  queue.length = 0;
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
}

if (typeof document !== 'undefined') {
  // `visibilitychange` rather than `unload`: mobile Safari often never fires
  // unload, so a child closing the PWA would lose the batch.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flush();
  });
}
